import React from 'react';
import { observer } from 'mobx-react';
import { Step } from '../common/Step';
import { StepText } from '../common/StepText';
import { StepSection } from '../common/StepSection';
import { FormButtonWrapper } from '../common/FormButtonWrapper';
import { Button } from '../../../commonv2/components/Button';
import { GlobalLoader } from '../../components/GlobalLoader';
import { UserInfoConfirmation } from '../common/userInfo/UserInfoConfirmation';
import './confirmationStep.module.scss';
import { CardPaymentConfirmation } from '../common/payment/CardPaymentConfirmation';
import { EventReservationPageState } from './states/EventReservationPageState';
import { EventSummary } from './components/EventSummary';
import { CheckBox } from '@sheinc/design-system';
import {
  PRIVACY_POLICY_URL,
  SHEID_AGREEMENT_URL,
} from '../../../mainv3/components/atoms/trial/reservation/ReservationNote';
import { GlobalStatesV3 } from '../../../commonv3/states/GlobalStatesV3';

type PrivacyPolicyNoteProps = {
  agreed: boolean;
  // INNOVATIVE WOMEN'S SUMMIT のみ個別の利用規約へ同意を求める
  // https://sheinc.atlassian.net/browse/SHE-5084
  isIWS: boolean;
  toggle: () => void;
};

const PrivacyPolicyNote = ({ agreed, toggle, isIWS }: PrivacyPolicyNoteProps) => {
  const isSignedIn = GlobalStatesV3.currentUser.isSignedIn();
  const note = isSignedIn ? (
    'イベントページと備考のご案内をご確認いただいた後、下記にチェックを付け、お申込を確定してください'
  ) : (
    <>
      {' '}
      <a styleName="link" target="_blank" href="https://she-inc.jp/common/policy.html">
        プライバシーポリシー
      </a>
      と
      <a styleName="link" target="_blank" href={SHEID_AGREEMENT_URL}>
        SHEID利用規約
      </a>
      にご同意いただける場合は、「同意する」にチェックを付け、お申込を確定してください
    </>
  );
  // INNOVATIVE WOMEN'S SUMMIT のみ個別の利用規約へ同意を求める
  // https://sheinc.atlassian.net/browse/SHE-5084
  const confirmMessage = isSignedIn ? (
    isIWS ? (
      <>
        <a styleName="link" target="_blank" href="https://she-inc.jp/common/agreement_iws.html">
          サミット利用規約
        </a>
        に同意し、イベントページと備考の案内を確認しました
      </>
    ) : (
      'イベントページと備考の案内を確認しました'
    )
  ) : isIWS ? (
    <>
      <a styleName="link" target="_blank" href="https://she-inc.jp/common/agreement_iws.html">
        サミット利用規約
      </a>
      、
      <a styleName="link" target="_blank" href={PRIVACY_POLICY_URL}>
        プライバシーポリシーとSHEID利用規約
      </a>
      に同意する
    </>
  ) : (
    <>
      <a styleName="link" target="_blank" href={PRIVACY_POLICY_URL}>
        プライバシーポリシーとSHEID利用規約
      </a>
      に同意する
    </>
  );
  return (
    <div styleName="privacy-policy-note">
      <div styleName="note">
        {note}
        {/* INNOVATIVE WOMEN'S SUMMIT のみ個別の利用規約へ同意を求める */}
        {/* https://sheinc.atlassian.net/browse/SHE-5084 */}
        {isIWS && (
          <>
            <br />
            また、このイベントの参加には
            <a styleName="link" target="_blank" href="https://she-inc.jp/common/agreement_iws.html">
              サミット利用規約
            </a>
            への同意が必要です
          </>
        )}
      </div>
      <div styleName="check-box-wrapper">
        <CheckBox checked={agreed} onCheck={toggle} id="agreement-confirmation">
          {confirmMessage}
        </CheckBox>
      </div>
    </div>
  );
};

type Props = {
  pageState: EventReservationPageState;
};

export const ConfirmationStep = observer(
  class ConfirmationStep extends React.Component<Props> {
    render() {
      const { pageState } = this.props;
      const {
        event: eventResource,
        appliedPrice,
        privacyPolicyAgreed,
        togglePrivacyPolicyAgreed,
        userEventReservationStatus,
      } = pageState;
      if (eventResource.isEmpty()) {
        throw new Error("InputStep shouldn't be rendered before initialization complete");
      }
      const event = eventResource.get();
      const needPayment = appliedPrice > 0;
      return (
        <Step id="confirmation-step">
          <StepSection title="予約情報の確認" styleName="section">
            {userEventReservationStatus !== null && (
              <EventSummary event={event} userEventReservationStatus={userEventReservationStatus} />
            )}
          </StepSection>
          {pageState.userForm.needUserInput() && (
            <StepSection title="会員情報" styleName="section">
              <UserInfoConfirmation state={pageState.userForm} />
            </StepSection>
          )}
          {needPayment && (
            <StepSection title="お支払い方法" styleName="section">
              <CardPaymentConfirmation
                state={pageState.paymentMethodForm}
                priceWithoutTax={event.price}
                priceWithTax={event.price_with_tax}
                onModificationRequested={() => pageState.goBackToInput()}
              />
            </StepSection>
          )}
          {event.note && (
            <StepSection title="備考" styleName="section">
              <StepText>{event.note}</StepText>
            </StepSection>
          )}
          {needPayment && (
            <StepSection title="キャンセルについて">
              <StepText>
                イベント開催２週間前からキャンセル料が発生するため、ご注意ください。
                <br />
                <br />
                キャンセル料に関しては以下です。
                <br />
                開催日14日前～8日前まで：参加費の50％
                <br />
                開催日7日前～前々日まで：参加費の80％
                <br />
                開催日の前日以降：参加費の100％
                <br />
              </StepText>
            </StepSection>
          )}
          <PrivacyPolicyNote agreed={privacyPolicyAgreed} toggle={togglePrivacyPolicyAgreed} isIWS={pageState.isIWS} />
          <FormButtonWrapper>
            <Button
              id="submit-button"
              type="primary"
              disabled={!privacyPolicyAgreed || pageState.processing}
              onClick={() => pageState.submit()}
            >
              確定する
            </Button>
          </FormButtonWrapper>
          {pageState.processing && <GlobalLoader />}
        </Step>
      );
    }
  }
);
